import { atom } from "jotai";

export interface VideoGenerationItem {
  predictionId: string;
  imageUrl: string;
  prompt: string;
  status: "processing" | "succeeded" | "failed";
  createdAt: string;
  completedAt?: string;
  outputUrl?: string;
  model?: string;
  imageId?: number;
  adAssetGroupId?: string;
  aspectRatio?: string;
}

export interface VideoGenerationState {
  activeGenerations: VideoGenerationItem[];
  completedGenerations: VideoGenerationItem[];
}

const initialState: VideoGenerationState = {
  activeGenerations: [],
  completedGenerations: []
};

// Base atom for video generation state
export const videoGenerationAtom = atom<VideoGenerationState>(initialState);

// Derived atom for adding a new video generation
export const addVideoGenerationAtom = atom(
  null,
  (get, set, newGeneration: Omit<VideoGenerationItem, "createdAt" | "status">) => {
    const current = get(videoGenerationAtom);
    const videoItem: VideoGenerationItem = {
      ...newGeneration,
      status: "processing",
      createdAt: new Date().toISOString()
    };

    set(videoGenerationAtom, {
      ...current,
      activeGenerations: [...current.activeGenerations, videoItem]
    });
  }
);

// Derived atom for updating video generation status
export const updateVideoGenerationStatusAtom = atom(
  null,
  (get, set, update: { predictionId: string; status: VideoGenerationItem["status"]; outputUrl?: string }) => {
    const current = get(videoGenerationAtom);

    const updatedActive = current.activeGenerations.map((item) => {
      if (item.predictionId === update.predictionId) {
        const updatedItem = {
          ...item,
          status: update.status,
          ...(update.outputUrl && { outputUrl: update.outputUrl }),
          ...(update.status === "succeeded" && { completedAt: new Date().toISOString() })
        };
        return updatedItem;
      }
      return item;
    });

    // Move completed items to completed list
    const stillActive = updatedActive.filter((item) => item.status !== "succeeded" && item.status !== "failed");
    const newCompleted = updatedActive.filter((item) => item.status === "succeeded" || item.status === "failed");

    set(videoGenerationAtom, {
      activeGenerations: stillActive,
      completedGenerations: [...current.completedGenerations, ...newCompleted]
    });
  }
);

// Derived atom for removing a completed generation
export const removeCompletedVideoAtom = atom(null, (get, set, predictionId: string) => {
  const current = get(videoGenerationAtom);
  set(videoGenerationAtom, {
    ...current,
    completedGenerations: current.completedGenerations.filter((item) => item.predictionId !== predictionId)
  });
});

// Derived atom for getting active prediction IDs for polling
export const activeVideoGenerationsAtom = atom((get) => {
  const state = get(videoGenerationAtom);
  return state.activeGenerations;
});

// Derived atom for getting completed generations
export const completedVideoGenerationsAtom = atom((get) => {
  const state = get(videoGenerationAtom);
  return state.completedGenerations;
});
